import {assert} from 'console';
import reactUpdate from 'immutability-helper';

import {getLocationInfoErrors} from 'molbio/bulk-assembly/assemble-modal/FinalizationUtil';
import {LocationInformation, DEFAULT_SAVE_INFORMATION} from 'molbio/components/SaveLocationPicker';
import {TableSelection} from 'notebook/structured-tables/components/EntryTableDropdown';
import {StudySearchItem} from 'studies/StudySearchInput';
import {StepItemStatus} from 'ui-library/StepNav';
import {assertNever} from 'util/Types';
import {WorklistFormType} from 'worklists/WorklistConstants';

export enum PerformAssemblyModalStep {
  DEFINE_OUTPUTS = 'DEFINE_OUTPUTS',
  REVIEW_AND_FINALIZE = 'REVIEW_AND_FINALIZE',
}

export enum FieldMappingType {
  ALL_INPUT_FRAGMENTS = 'ALL_INPUT_FRAGMENTS',
  ALL_OUTPUT_FRAGMENTS = 'ALL_OUTPUT_FRAGMENTS',
  INPUT_FRAGMENT_BIN = 'INPUT_FRAGMENT_BIN',
  OUTPUT_FRAGMENT_BIN = 'OUTPUT_FRAGMENT_BIN',
  FRAGMENT_PRIMERS = 'FRAGMENT_PRIMERS',
  OTHER_PRIMERS = 'OTHER_PRIMERS',
  FIVE_PRIME_PRIMER = 'FIVE_PRIME_PRIMER',
  THREE_PRIME_PRIMER = 'THREE_PRIME_PRIMER',
}

export type FieldMappingTarget =
  | {type: FieldMappingType.FRAGMENT_PRIMERS}
  | {type: FieldMappingType.OTHER_PRIMERS}
  | {type: FieldMappingType.FIVE_PRIME_PRIMER}
  | {type: FieldMappingType.THREE_PRIME_PRIMER}
  | {type: FieldMappingType.ALL_INPUT_FRAGMENTS}
  | {type: FieldMappingType.ALL_OUTPUT_FRAGMENTS}
  | {type: FieldMappingType.INPUT_FRAGMENT_BIN; binId: string}
  | {type: FieldMappingType.OUTPUT_FRAGMENT_BIN; binId: string};

export interface LocationInformationWithFieldMapping extends LocationInformation {
  fieldMapping?: {[fieldId: string]: FieldMappingTarget};
}

export interface PerformAssemblyModalState {
  stepKey: PerformAssemblyModalStep;
  statusByStep: {
    [key in PerformAssemblyModalStep]: StepItemStatus | null;
  };
  saveFragments: boolean;
  fragmentLocationInfo: LocationInformationWithFieldMapping;
  constructLocationInfo: LocationInformationWithFieldMapping;
  primerLocationInfo: LocationInformationWithFieldMapping;
  selectedFragmentBinId: string | null;
  savePrimers: boolean;
  currentStepValidationErrors: Array<string>;

  selectedEntryId: string | null;
  selectedConstructTable: TableSelection;
  selectedPrimerTable: TableSelection;
  selectedFragmentTable: TableSelection;

  addOutputsToEntry: boolean;
  addOutputsToWorklist: boolean;

  selectedStudies: Array<StudySearchItem>;
}

export function getInitialReducerState(
  initialFolderId: string | null,
  canCreatePrimers: boolean
): PerformAssemblyModalState {
  const initialSaveInformation: LocationInformationWithFieldMapping = {
    ...DEFAULT_SAVE_INFORMATION,
    targetFolderId: initialFolderId ?? undefined,
    tagSchemaId: null,
  };

  const initialState = {
    stepKey: PerformAssemblyModalStep.DEFINE_OUTPUTS,
    statusByStep: {
      [PerformAssemblyModalStep.DEFINE_OUTPUTS]: StepItemStatus.UNVISITED,
      [PerformAssemblyModalStep.REVIEW_AND_FINALIZE]: StepItemStatus.UNVISITED,
    },
    saveFragments: false,
    fragmentLocationInfo: initialSaveInformation,
    constructLocationInfo: initialSaveInformation,
    primerLocationInfo: initialSaveInformation,
    selectedFragmentBinId: null,
    savePrimers: canCreatePrimers,
    currentStepValidationErrors: [],
    selectedEntryId: null,
    selectedConstructTable: null,
    selectedPrimerTable: null,
    selectedFragmentTable: null,
    addOutputsToEntry: false,
    addOutputsToWorklist: false,
    selectedStudies: [],
  };

  return computeAndApplyValidation(initialState);
}

interface SetStepKeyAction {
  type: 'SET_STEP_KEY';
  value: PerformAssemblyModalStep;
}

interface SetStatusByStepAction {
  type: 'SET_STATUS_BY_STEP';
  value: {[key in PerformAssemblyModalStep]: StepItemStatus | null};
}

interface SetSaveFragmentsAction {
  type: 'SET_SAVE_FRAGMENTS';
  value: boolean;
}

interface SetFragmentLocationInfoAction {
  type: 'SET_FRAGMENT_LOCATION_INFO';
  value: LocationInformation;
}

interface SetSelectedFragmentBinIdAction {
  type: 'SET_SELECTED_FRAGMENT_BIN_ID';
  value: string | null;
}

interface SetConstructLocationInfoAction {
  type: 'SET_CONSTRUCT_LOCATION_INFO';
  value: LocationInformation;
}

interface SetSavePrimersAction {
  type: 'SET_SAVE_PRIMERS';
  value: boolean;
}

interface SetPrimerLocationInfoAction {
  type: 'SET_PRIMER_LOCATION_INFO';
  value: LocationInformation;
}

interface UpdateTagSchemaAction {
  type: 'UPDATE_TAG_SCHEMA';
  entityType: 'construct' | 'fragment' | 'primer';
  tagSchemaId: string | null;
}

interface UpdateFieldMappingAction {
  type: 'UPDATE_FIELD_MAPPING';
  entityType: 'construct' | 'fragment' | 'primer';
  fieldId: string;
  target: FieldMappingTarget | null;
}

interface SetSelectedEntryIdAction {
  type: 'SET_SELECTED_ENTRY_ID';
  value: string | null;
}

interface SetSelectedTableAction {
  type: 'SET_SELECTED_TABLE';
  entityType: 'construct' | 'primer' | 'fragment';
  value: TableSelection;
}

interface SetFolderIdAction {
  type: 'SET_FOLDER_ID';
  value: string | null;
}

interface SetSelectedStudiesAction {
  type: 'SET_SELECTED_STUDIES';
  value: Array<StudySearchItem>;
}

interface SetAddOutputsToEntryAction {
  type: 'SET_ADD_OUTPUTS_TO_ENTRY';
  value: boolean;
}

interface SetAddOutputsToWorklistAction {
  type: 'SET_ADD_OUTPUTS_TO_WORKLIST';
  value: boolean;
}

interface SetSelectedWorklistIdAction {
  type: 'SET_SELECTED_WORKLIST_ID';
  entityType: 'construct' | 'primer' | 'fragment';
  worklistId: string | null;
}

interface SetNewWorklistNameAction {
  type: 'SET_NEW_WORKLIST_NAME';
  entityType: 'construct' | 'primer' | 'fragment';
  worklistName: string;
}

export type PerformAssemblyModalAction =
  | SetStepKeyAction
  | SetStatusByStepAction
  | SetSaveFragmentsAction
  | SetFragmentLocationInfoAction
  | SetSelectedFragmentBinIdAction
  | SetConstructLocationInfoAction
  | SetSavePrimersAction
  | SetPrimerLocationInfoAction
  | UpdateTagSchemaAction
  | UpdateFieldMappingAction
  | SetSelectedEntryIdAction
  | SetSelectedTableAction
  | SetFolderIdAction
  | SetSelectedStudiesAction
  | SetAddOutputsToEntryAction
  | SetAddOutputsToWorklistAction
  | SetSelectedWorklistIdAction
  | SetNewWorklistNameAction;

export const ENTITY_TYPE_TO_LOCATION_KEY: Record<
  'construct' | 'fragment' | 'primer',
  'constructLocationInfo' | 'fragmentLocationInfo' | 'primerLocationInfo'
> = {
  construct: 'constructLocationInfo',
  fragment: 'fragmentLocationInfo',
  primer: 'primerLocationInfo',
};

const ENTITY_TYPE_TO_TABLE_KEY: Record<
  'construct' | 'primer' | 'fragment',
  'selectedConstructTable' | 'selectedPrimerTable' | 'selectedFragmentTable'
> = {
  construct: 'selectedConstructTable',
  primer: 'selectedPrimerTable',
  fragment: 'selectedFragmentTable',
};

function computeAndApplyValidation(state: PerformAssemblyModalState): PerformAssemblyModalState {
  let errors: Array<string> = [];
  if (state.stepKey === PerformAssemblyModalStep.REVIEW_AND_FINALIZE) {
    errors = [
      ...(state.saveFragments
        ? getLocationInfoErrors({
            locationInfo: state.fragmentLocationInfo,
            displayString: 'fragments',
          })
        : []),
      ...(state.savePrimers
        ? getLocationInfoErrors({locationInfo: state.primerLocationInfo, displayString: 'primers'})
        : []),
      ...getLocationInfoErrors({locationInfo: state.constructLocationInfo, displayString: 'constructs'}),
    ];

    if (state.addOutputsToEntry && !state.selectedEntryId) {
      errors.push('An entry is required when adding outputs to a notebook entry.');
    }
    if (!state.addOutputsToEntry) {
      assert(!state.selectedConstructTable, 'Cannot add constructs to a table without selecting an entry.');
      assert(!state.selectedPrimerTable, 'Cannot add primers to a table without selecting an entry.');
      assert(!state.selectedFragmentTable, 'Cannot add fragments to a table without selecting an entry.');
    }
  }

  return reactUpdate(state, {
    currentStepValidationErrors: {$set: errors},
    statusByStep: {
      $merge: {
        [state.stepKey]: StepItemStatus.UNVISITED, // Avoid showing error status.
      },
    },
  });
}

export default function performAssemblyModalReducer(
  state: PerformAssemblyModalState,
  action: PerformAssemblyModalAction
): PerformAssemblyModalState {
  switch (action.type) {
    case 'SET_STEP_KEY': {
      const newState = reactUpdate(state, {
        stepKey: {$set: action.value},
      });
      return computeAndApplyValidation(newState);
    }
    case 'SET_STATUS_BY_STEP': {
      return reactUpdate(state, {
        statusByStep: {$set: action.value},
      });
    }
    case 'SET_SAVE_FRAGMENTS': {
      const newState = reactUpdate(
        state,
        action.value
          ? {
              saveFragments: {$set: true},
            }
          : {
              saveFragments: {$set: false},
              fragmentLocationInfo: {
                $set: {
                  ...state.fragmentLocationInfo,
                  tagSchemaId: null,
                },
              },
              selectedFragmentBinId: {$set: null},
            }
      );
      return computeAndApplyValidation(newState);
    }
    case 'SET_FRAGMENT_LOCATION_INFO': {
      const newState = reactUpdate(state, {
        fragmentLocationInfo: {$set: action.value},
      });
      return computeAndApplyValidation(newState);
    }
    case 'SET_SELECTED_FRAGMENT_BIN_ID': {
      return reactUpdate(state, {
        selectedFragmentBinId: {$set: action.value},
      });
    }
    case 'SET_CONSTRUCT_LOCATION_INFO': {
      const newState = reactUpdate(state, {
        constructLocationInfo: {$set: action.value},
      });
      return computeAndApplyValidation(newState);
    }
    case 'SET_SAVE_PRIMERS': {
      const newState = reactUpdate(
        state,
        action.value
          ? {
              savePrimers: {$set: true},
            }
          : {
              savePrimers: {$set: false},
              primerLocationInfo: {
                $set: {
                  ...state.primerLocationInfo,
                  tagSchemaId: null,
                },
              },
            }
      );
      return computeAndApplyValidation(newState);
    }
    case 'SET_PRIMER_LOCATION_INFO': {
      const newState = reactUpdate(state, {
        primerLocationInfo: {$set: action.value},
      });
      return computeAndApplyValidation(newState);
    }
    case 'UPDATE_TAG_SCHEMA': {
      const {entityType, tagSchemaId} = action;
      const locationKey = ENTITY_TYPE_TO_LOCATION_KEY[entityType];
      const currentLocationInfo = state[locationKey];
      const currentSchemaId = currentLocationInfo.tagSchemaId;
      const newSchemaId = tagSchemaId === currentSchemaId ? null : (tagSchemaId ?? null);

      const newState = reactUpdate(state, {
        [locationKey]: {
          $set: {
            ...currentLocationInfo,
            tagSchemaId: newSchemaId,
          },
        },
      });
      return computeAndApplyValidation(newState);
    }
    case 'UPDATE_FIELD_MAPPING': {
      const {entityType, fieldId, target} = action;
      const locationKey = ENTITY_TYPE_TO_LOCATION_KEY[entityType];
      const currentFieldMapping = state[locationKey].fieldMapping ?? {};

      let updatedFieldMapping: {[fieldId: string]: FieldMappingTarget};
      if (target == null) {
        // Remove the field from the field mapping if it exists.
        const {[fieldId]: _, ...rest} = currentFieldMapping;
        updatedFieldMapping = rest;
      } else {
        updatedFieldMapping = reactUpdate(currentFieldMapping, {$merge: {[fieldId]: target}});
      }

      const newState = reactUpdate(state, {
        [locationKey]: {
          $merge: {
            fieldMapping: updatedFieldMapping,
          },
        },
      });
      return computeAndApplyValidation(newState);
    }
    case 'SET_SELECTED_ENTRY_ID': {
      const newState = reactUpdate(state, {
        selectedEntryId: {$set: action.value},
        // Clear table selections when entry changes
        selectedConstructTable: {$set: null},
        selectedPrimerTable: {$set: null},
        selectedFragmentTable: {$set: null},
      });
      return computeAndApplyValidation(newState);
    }
    case 'SET_SELECTED_TABLE': {
      const {entityType, value} = action;
      const tableKey = ENTITY_TYPE_TO_TABLE_KEY[entityType];
      const newState = reactUpdate(state, {
        [tableKey]: {$set: value},
      });
      return computeAndApplyValidation(newState);
    }
    case 'SET_FOLDER_ID': {
      return reactUpdate(state, {
        fragmentLocationInfo: {
          $merge: {
            targetFolderId: action.value ?? undefined,
          },
        },
        constructLocationInfo: {
          $merge: {
            targetFolderId: action.value ?? undefined,
          },
        },
        primerLocationInfo: {
          $merge: {
            targetFolderId: action.value ?? undefined,
          },
        },
      });
    }
    case 'SET_SELECTED_STUDIES': {
      return reactUpdate(state, {
        selectedStudies: {$set: action.value},
      });
    }
    case 'SET_ADD_OUTPUTS_TO_ENTRY': {
      const newState = reactUpdate(state, {
        addOutputsToEntry: {$set: action.value},
      });
      return computeAndApplyValidation(newState);
    }
    case 'SET_ADD_OUTPUTS_TO_WORKLIST': {
      const newState = reactUpdate(state, {
        addOutputsToWorklist: {$set: action.value},
        fragmentLocationInfo: {
          worklistParams: {$set: !action.value ? null : state.fragmentLocationInfo.worklistParams},
        },
        primerLocationInfo: {
          worklistParams: {$set: !action.value ? null : state.primerLocationInfo.worklistParams},
        },
        constructLocationInfo: {
          worklistParams: {$set: !action.value ? null : state.constructLocationInfo.worklistParams},
        },
      });
      return computeAndApplyValidation(newState);
    }
    case 'SET_SELECTED_WORKLIST_ID': {
      const {entityType, worklistId} = action;
      const locationKey = ENTITY_TYPE_TO_LOCATION_KEY[entityType];
      const newState = reactUpdate(state, {
        [locationKey]: {
          $merge: {
            worklistParams: worklistId
              ? {
                  worklistFormType: WorklistFormType.EXISTING,
                  targetWorklistId: worklistId,
                  newWorklistInfo: null,
                }
              : null,
          },
        },
      });
      return computeAndApplyValidation(newState);
    }
    case 'SET_NEW_WORKLIST_NAME': {
      const {entityType, worklistName} = action;
      const locationKey = ENTITY_TYPE_TO_LOCATION_KEY[entityType];
      const newState = reactUpdate(state, {
        [locationKey]: {
          $merge: {
            worklistParams: {
              worklistFormType: WorklistFormType.NEW,
              targetWorklistId: null,
              newWorklistInfo: {name: worklistName},
            },
          },
        },
      });
      return computeAndApplyValidation(newState);
    }
    default:
      assertNever(action);
      return state;
  }
}
